import {
  CampaignInfluencerReport,
  CampaignSummaryDaily,
  MarketplaceCampaignReport,
  NewCampaignReportSummary,
  NewCampaignStatsItemRow
} from '@anytag/graphql';
import {
  PostReportStats,
  PostReportSegmentData,
  PostReportSegmentDataType,
  PostReportSegment,
  PostReportRowData
} from './types';

export const getPostReportStats = (
  summaries?: NewCampaignStatsItemRow | null,
  insightDataAcquisition: string | null = null
): PostReportStats => ({
  insightDataAcquisition,
  cost: summaries?.cost ?? null,
  like: summaries?.like ?? null,
  view: summaries?.view ?? null,
  back: summaries?.back ?? null,
  share: summaries?.share ?? null,
  reach: summaries?.reach ?? null,
  saved: summaries?.saved ?? null,
  click: summaries?.click ?? null,
  sales: summaries?.sales ?? null,
  exited: summaries?.exited ?? null,
  retweets: summaries?.share ?? null, // Retweets get data from share
  comment: summaries?.comment ?? null,
  forward: summaries?.forward ?? null,
  dislikes: summaries?.dislikes ?? null,
  reachRate: summaries?.reachRate ?? null,
  followers: summaries?.followers ?? null,
  nextStory: summaries?.nextStory ?? null,
  conversion: summaries?.conversion ?? null,
  engagement: summaries?.engagement ?? null,
  linkClicks: summaries?.linkClicks ?? null,
  navigation: summaries?.navigation ?? null,
  impressions: summaries?.impressions ?? null,
  stickerTaps: summaries?.stickerTaps ?? null,
  interaction: summaries?.interaction ?? null,
  costsPerLike: summaries?.costsPerLike ?? null,
  costsPerView: summaries?.costsPerView ?? null,
  costsPerSale: summaries?.costsPerSale ?? null,
  newFollowers: summaries?.newFollowers ?? null,
  followerReach: summaries?.followerReach ?? null,
  costsPerShare: summaries?.costsPerShare ?? null,
  costsPerClick: summaries?.costsPerClick ?? null,
  costsPerReach: summaries?.costsPerReach ?? null,
  costsPerSaved: summaries?.costsPerSaved ?? null,
  totalPlayTime: summaries?.totalPlayTime ?? null,
  fullVideoViews: summaries?.fullVideoViews ?? null,
  engagementRate: summaries?.engagementRate ?? null,
  engagementViewsRate: summaries?.engagementViewsRate ?? null,
  impressionRate: summaries?.impressionRate ?? null,
  malePercentage: summaries?.malePercentage ?? null,
  costsPerAction: summaries?.costsPerAction ?? null,
  costsPerComment: summaries?.costsPerComment ?? null,
  averageViewTime: summaries?.averageViewTime ?? null,
  profileActivity: summaries?.profileActivity ?? null,
  nonFollowerReach: summaries?.nonFollowerReach ?? null,
  costsPerFollower: summaries?.costsPerFollower ?? null,
  femalePercentage: summaries?.femalePercentage ?? null,
  followerReachRate: summaries?.followerReachRate ?? null,
  age1824Percentage: summaries?.age1824Percentage ?? null,
  age2534Percentage: summaries?.age2534Percentage ?? null,
  age35UpPercentage: summaries?.age35UpPercentage ?? null,
  costsPerEngagement: summaries?.costsPerEngagement ?? null,
  videoCompletionRate: summaries?.videoCompletionRate ?? null,
  costsPerImpressions: summaries?.costsPerImpressions ?? null,
  impressionsFromHome: summaries?.impressionsFromHome ?? null,
  impressionsFromOther: summaries?.impressionsFromOther ?? null,
  nonFollowerReachRate: summaries?.nonFollowerReachRate ?? null,
  impressionsFromHashtag: summaries?.impressionsFromHashtag ?? null,
  impressionsFromProfile: summaries?.impressionsFromProfile ?? null,
  impressionsFromDiscovery: summaries?.impressionsFromDiscovery ?? null,
  averageViewDurationMinutes: summaries?.averageViewDurationMinutes ?? null,
  averageViewDurationPercentage: summaries?.averageViewDurationPercentage ?? null,
  viewsFromHome: summaries?.viewsFromHome ?? null,
  viewsFromOther: summaries?.viewsFromOther ?? null,
  viewsFromSearch: summaries?.viewsFromSearch ?? null,
  viewsFromProfile: summaries?.viewsFromProfile ?? null,
  viewsFromDiscovery: summaries?.viewsFromDiscovery ?? null,
  viewsFromFollowing: summaries?.viewsFromFollowing ?? null,
  accountsEngaged: summaries?.accountsEngaged ?? null,
  avgWatchTimes: summaries?.avgWatchTimes ?? null,
  exploreViewsRate: summaries?.exploreViewsRate ?? null,
  feedViewsRate: summaries?.feedViewsRate ?? null,
  searchViewsRate: summaries?.searchViewsRate ?? null,
  profileVisits: summaries?.profileVisits ?? null,
  follows: summaries?.follows ?? null,
  reelSkipRate: summaries?.reelSkipRate ?? null,
  typicalSkipRate: summaries?.typicalSkipRate ?? null,
  totalWatchTimes: summaries?.totalWatchTimes ?? null,
  replies: summaries?.replies ?? null,
  quotes: summaries?.quotes ?? null,
  relateComments: summaries?.relateComments ?? null,
  relateRate: summaries?.relateRate ?? null,
  viewsByFollower: summaries?.viewsByFollower ?? null,
  viewsByNonFollower: summaries?.viewsByNonFollower ?? null,
  profileViewsRate: summaries?.profileViewsRate ?? null,
  storyViewsRate: summaries?.storyViewsRate ?? null,
  audienceRetention: summaries?.audienceRetention ?? null,
  avgPlayRate: summaries?.avgPlayRate ?? null,
  detailExpands: summaries?.detailExpands ?? null,
  impressionsClickThroughRate: summaries?.impressionsClickThroughRate ?? null,
  viewRate: summaries?.viewRate ?? null
});

// Format segment data
export const formatPostReportSegmentTotal = (data?: readonly CampaignSummaryDaily[] | null) =>
  data?.map<PostReportSegmentDataType>(({ date, stats }) => ({ date, stats: getPostReportStats(stats) })) || [];

export const formatPostReportSegmentInfluencerPostDaily = (
  data?: readonly CampaignInfluencerReport[] | null
): PostReportSegmentData['post'] =>
  data?.reduce<PostReportSegmentData['post']>(
    (results, { postReports }) => ({
      ...results,
      ...(postReports?.reduce<PostReportSegmentData['post']>(
        (posts, { postInfo: { id }, orderedSummaryDaily }) => ({
          ...posts,
          [id]:
            orderedSummaryDaily?.map<PostReportSegmentDataType>(({ date, stats }) => ({
              date,
              stats: getPostReportStats(stats)
            })) || []
        }),
        {}
      ) || {})
    }),
    {}
  ) || null;

export const formatPostReportSegmentInfluencerDaily = (
  data?: readonly CampaignInfluencerReport[] | null
): PostReportSegmentData['influencer'] =>
  data?.reduce<PostReportSegmentData['influencer']>(
    (results, { influencerInfo, orderedSummaryDaily }) => ({
      ...results,
      [influencerInfo.id]:
        orderedSummaryDaily?.map<PostReportSegmentDataType>(({ date, stats }) => ({
          date,
          stats: getPostReportStats(stats)
        })) || []
    }),
    {}
  ) || null;

export const formatPostReportData = ({
  segment,
  summaryData,
  data: { influencerReports }
}: {
  segment: PostReportSegment;
  data: MarketplaceCampaignReport;
  summaryData?: NewCampaignReportSummary;
}) => {
  const rows: PostReportRowData[] = [
    { className: 'total-row', stats: getPostReportStats(summaryData?.totalSummary) }, // Total row
    { className: 'average-row', stats: getPostReportStats(summaryData?.totalAverage) } // Average row
  ];

  // Add segment total rows if segment total is enabled
  if (segment.total) {
    rows.push(
      ...segment.total.data.map<PostReportRowData>(({ date, stats }) => ({
        date,
        stats,
        className: 'total-daily-row'
      }))
    );
  }

  // Influencer rows
  influencerReports.forEach(({ influencerInfo, summary, postReports }) => {
    const postLength = postReports?.length || 0;
    const firstPost = postReports?.at(0)?.postInfo;

    // Add influencer row
    rows.push({
      influencerInfo: {
        firstPost,
        postLength,
        id: influencerInfo.id,
        name: influencerInfo.name,
        thumbNail: influencerInfo.thumbNail
      },
      stats: getPostReportStats(summary, postLength <= 1 ? firstPost?.insightDataAcquisition : undefined)
    });

    // Add segment influencer rows if segment influencer is enabled
    if (segment.influencer?.data && segment.influencer.data[influencerInfo.id]?.length) {
      rows.push(
        ...segment.influencer.data[influencerInfo.id].map<PostReportRowData>(({ date, stats }) => ({
          date,
          stats,
          className: 'post-daily-row'
        }))
      );
    }

    // Add post rows
    if (postLength > 1) {
      postReports?.forEach(({ postInfo, summary: postSummary }) => {
        // Add post row
        rows.push({ postInfo, stats: getPostReportStats(postSummary, postInfo.insightDataAcquisition) });

        // Add segment post rows if segment post is enabled
        if (segment.post?.data && segment.post.data[postInfo.id]?.length) {
          rows.push(
            ...segment.post.data[postInfo.id].map<PostReportRowData>(({ date, stats }) => ({
              date,
              stats,
              className: 'post-daily-row'
            }))
          );
        }
      });
    }
  });

  return rows;
};
